/**
 * Intent detection and inline calendar helpers for automatic booking
 */

/**
 * Check if user message contains booking intent keywords
 */
export function detectBookingIntent(message: string): boolean {
  const lowerMessage = message.toLowerCase().trim();

  const bookingKeywords = [
    'book',
    'booking',
    'schedule',
    'scheduling',
    'appointment',
    'meeting',
    'call',
    'demo',
    'consultation',
    'session',
    'reserve',
    'reservation',
    'calendar',
    'availability',
    'time slot',
    'available time',
    'meet with',
    'talk to',
    'speak with',
    'discuss',
    'calendly',
    'set up meeting',
    'set up a call',
    'set up call',
    'set an appointment',
    'set meeting',
    'arrange a meeting',
    'arrange meeting',
    'arrange call',
    'arrange appointment',
    'book a slot',
    'book slot',
    'slot booking',
    'grab a slot',
    'reserve slot',
    'pick a time',
    'choose a time',
    'find a time',
    'schedule time',
    'schedule a chat',
    'schedule chat',
    'schedule discussion',
    'schedule demo',
    'schedule meeting',
    'schedule call',
    'connect',
    'connect with',
    'jump on a call',
    'hop on a call',
    'quick call',
    'quick chat',
    '1:1 call',
    'one on one call',
    'team call',
    'follow-up call',
    'follow up call',
    'appointment slot',
    'meeting slot',
    'call slot',
    'open slot',
    'time to talk',
    'time to meet',
    'when are you free',
    'when can we talk',
    'when can we meet',
    'book time',
    'booking link',
    'meeting link',
    'call link',
    'demo call',
    'product demo',
    'intro call',
    'discovery call',
    'sales call',
    'consulting call',
    'strategy call',
    'audit call',
    'schedule now',
    'book now',
    'reserve now',
    'make an appointment',
    'fix a meeting',
    'fix meeting',
    'fix appointment',
    'can we talk',
    'can we meet',
    'can we schedule',
    'i want to book',
    'i want to schedule',
    'i need a meeting',
    'i need an appointment',
    'need to talk',
    'need to meet',
    'need a call',
    'calendar link',
    'meeting scheduler',
    'booking page',
    'schedule page',
    'appointment page',
  ];

  return bookingKeywords.some((keyword) => lowerMessage.includes(keyword));
}

/**
 * Create and append an inline Calendly widget in the chat interface
 */
export function appendInlineCalendlyWidget(
  messageContainer: HTMLDivElement,
  calendlyUrl: string,
  widgetElement: HTMLElement,
  onSkip?: () => void
): HTMLDivElement {
  // Expand the widget
  widgetElement.classList.add('calendar-expanded');

  // Store current messages before clearing
  storeChatMessages(messageContainer);

  // Clear the messages container and make it full height
  messageContainer.style.height = '100%';
  messageContainer.style.overflow = 'hidden';
  messageContainer.style.padding = '0';

  // Remove all previous messages to show only calendar
  messageContainer.innerHTML = '';

  const wrapper = document.createElement('div');
  wrapper.className = 'calendly-inline-message';

  const calendlyContainer = document.createElement('div');
  calendlyContainer.className = 'inline-calendly-container';
  calendlyContainer.id = `inline-calendly-${Date.now()}`;

  // Add a professional header with skip button
  const header = document.createElement('div');
  header.className = 'inline-calendly-header';
  header.innerHTML = `
    <p>Select Your Preferred Meeting Time</p>
    <button class="calendly-skip-btn" title="Skip booking">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
  `;

  // Calendly widget wrapper
  const widgetWrapper = document.createElement('div');
  widgetWrapper.className = 'inline-calendly-widget';

  calendlyContainer.appendChild(header);
  calendlyContainer.appendChild(widgetWrapper);
  wrapper.appendChild(calendlyContainer);

  messageContainer.appendChild(wrapper);

  // Add skip button click handler
  const skipButton = header.querySelector('.calendly-skip-btn') as HTMLButtonElement;
  if (skipButton && onSkip) {
    skipButton.addEventListener('click', () => {
      onSkip();
    });
  }

  // Initialize Calendly inline widget with error handling
  if (window.Calendly && window.Calendly.initInlineWidget) {
    try {
      window.Calendly.initInlineWidget({
        url: calendlyUrl,
        parentElement: widgetWrapper,
      });
    } catch (error) {
      console.error('[Calendly] Error initializing widget:', error);
    }
  } else {
    console.error('[Calendly] Calendly library not loaded. Window.Calendly:', window.Calendly);
    // Show error message to user
    widgetWrapper.innerHTML = `
      <div style="padding: 2rem; text-align: center; color: #666;">
        <p>Unable to load calendar. Please refresh the page and try again.</p>
      </div>
    `;
  }

  return wrapper;
}

/**
 * Restore widget to normal size and restore chat view
 */
export function collapseWidget(widgetElement: HTMLElement, messageContainer: HTMLDivElement): void {
  widgetElement.classList.remove('calendar-expanded');
  messageContainer.style.height = '';
  messageContainer.style.overflow = '';
  messageContainer.style.padding = '';
}

/**
 * Store chat messages before clearing
 */
let storedMessages: string = '';

export function storeChatMessages(messageContainer: HTMLDivElement): void {
  // Remove any loading messages before storing
  const loadingMessages = messageContainer.querySelectorAll('.calendly-loading-message');
  loadingMessages.forEach((msg) => msg.remove());

  storedMessages = messageContainer.innerHTML;
}

/**
 * Restore previous chat messages
 */
export function restoreChatMessages(messageContainer: HTMLDivElement): void {
  if (storedMessages) {
    messageContainer.innerHTML = storedMessages;
    messageContainer.scrollTop = messageContainer.scrollHeight;
  }
}

/**
 * Show a message indicating that Calendly is being loaded
 */
export function showCalendlyLoadingMessage(messageContainer: HTMLDivElement): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'chat-message calendly-loading-message';

  const respondDiv = document.createElement('div');
  respondDiv.className = 'chat-bubble-ai chat-padding';

  respondDiv.innerHTML = `
    <p>Opening booking calendar for you...</p>
  `;

  wrapper.appendChild(respondDiv);
  messageContainer.appendChild(wrapper);
  messageContainer.scrollTop = messageContainer.scrollHeight;

  return wrapper;
}
